/**
 * Auto-translate hook - provides Google Translate-like functionality
 * Automatically translates any text based on the current language setting
 */

import { useCallback, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  translateText,
  translateOrganizationName,
  translateUnitType,
  translateStatus,
  translateBatch,
  addTranslations,
} from '../utils/dynamicTranslations';

/**
 * Hook for auto-translating dynamic content
 * Usage:
 *   const { translate, translateOrg, translateUnit } = useAutoTranslate();
 *   <Text>{translate('مرحبا')}</Text>
 *   <Text>{translateOrg(organization.name)}</Text>
 */
export const useAutoTranslate = () => {
  const { language } = useLanguage();

  // Main translate function - memoized for performance
  const translate = useCallback(
    (text: string | undefined | null): string => {
      return translateText(text, language);
    },
    [language]
  );

  // Translate organization name
  const translateOrg = useCallback(
    (name: string | undefined | null): string => {
      return translateOrganizationName(name, language);
    },
    [language]
  );

  // Translate unit type
  const translateUnit = useCallback(
    (type: string | undefined | null): string => {
      return translateUnitType(type, language);
    },
    [language]
  );

  // Translate status
  const translateStat = useCallback(
    (status: string | undefined | null): string => {
      return translateStatus(status, language);
    },
    [language]
  );

  // Batch translate
  const translateAll = useCallback(
    (texts: (string | undefined | null)[]): string[] => {
      return translateBatch(texts, language);
    },
    [language]
  );

  // Current language
  const isArabic = language === 'ar';
  const isEnglish = language === 'en';

  return useMemo(
    () => ({
      translate,
      translateOrg,
      translateUnit,
      translateStat,
      translateAll,
      addTranslations,
      language,
      isArabic,
      isEnglish,
    }),
    [translate, translateOrg, translateUnit, translateStat, translateAll, language, isArabic, isEnglish]
  );
};

export default useAutoTranslate;
